/* eslint-disable */
import { MeshStandardMaterial, Vector3 } from 'three';

import { isSystemHidden } from '../ModelFactory';

import * as Helpers from '../helpers';
import { Object3D } from '../Object3D';

export const braceStyle = {
  braced: 'L',
  crossed: 'X',
};

export default class EleganceDoor extends Object3D {
  constructor(...args) {
    super(...args);

    this.materials = {
      metal: new MeshStandardMaterial(),
      mirror: new MeshStandardMaterial(),
      woodBody: new MeshStandardMaterial(),
      woodFrame: new MeshStandardMaterial(),
    };

    this.parameters.frameThickness = 40;
    this.parameters.frameGroove = 20;

    this.parameters.mirrorThickness = 4;

    this.parameters.slatSize = 100;

    this.parameters.handleHoleWidth = 40;
    this.parameters.handleHoleLength = 160;
    this.parameters.handleHoleRadius = 50;
    this.parameters.handleHoleDepth = 12;
    this.parameters.handleHoleOffset = 15;
    this.parameters.handleOffset = 20;

    this.parameters.braceWidth = 120;
    this.parameters.braceThickness = 11;

    this.parameters.hangerMargin = 60;
    this.parameters.hangerOffset = -20;
  }

  calculateModelParameters() {
    this.parameters.frameWidth = this.parameters.width > 1200 ? 160 : 120;
    this.parameters.frameWidth = this.parameters.width < 700 ? 80 : 120;

    this.parameters.hangerMargin = isSystemHidden(this.parameters.system)
      ? 120
      : this.parameters.frameWidth / 2;
    this.parameters.hangerOffset = isSystemHidden(this.parameters.system)
      ? 20
      : -20;
    this.parameters.hangerHeightOffset = isSystemHidden(this.parameters.system)
      ? -100
      : 1;

    this.parameters.railWidth =
      this.parameters.width - 2 * this.parameters.frameWidth;

    this.parameters.slatThickness =
      this.parameters.style !== 'mirror' ? 18 : 14;
    this.parameters.slatCentering =
      this.parameters.style !== 'mirror'
        ? 0
        : this.parameters.slatThickness / 2;
    this.parameters.slatLength =
      (this.parameters.height -
        2 * (this.parameters.frameWidth - this.parameters.frameGroove) -
        this.parameters.density *
          (this.parameters.frameWidth - 2 * this.parameters.frameGroove)) /
      (this.parameters.density + 1);
    this.parameters.slatWidth =
      this.parameters.width -
      2 * (this.parameters.frameWidth - this.parameters.frameGroove);
  }

  buildFrameGeometry() {
    const geometries = [];

    /* create the base geometries */
    let leftStile = Helpers.CreateLeftStyle(
      this.parameters.frameWidth,
      this.parameters.height,
      this.parameters.frameThickness
    );
    const rightStile = Helpers.CreateRightStyle(
      this.parameters.frameWidth,
      this.parameters.height,
      this.parameters.frameThickness
    );
    const topRail = Helpers.CreateTopRail(
      this.parameters.frameWidth,
      this.parameters.railWidth,
      this.parameters.frameThickness,
      this.parameters.slatThickness,
      this.parameters.frameGroove
    );
    const bottomRail = Helpers.CreateBottomRail(
      this.parameters.frameWidth,
      this.parameters.railWidth,
      this.parameters.frameThickness,
      this.parameters.slatThickness,
      this.parameters.frameGroove
    );

    /* position the geometries */
    leftStile.translate(this.parameters.frameWidth / 2, 0, 0);
    rightStile.translate(
      this.parameters.width - this.parameters.frameWidth / 2,
      0,
      0
    );
    topRail.translate(
      this.parameters.frameWidth +
        this.parameters.railWidth / 2 -
        this.parameters.frameGroove,
      0,
      this.parameters.height - this.parameters.frameWidth
    );
    bottomRail.translate(
      this.parameters.frameWidth +
        this.parameters.railWidth / 2 -
        this.parameters.frameGroove,
      0,
      0
    );

    if( this.parameters.handle ) {
      /* create the hole for the handle */
      const handleHole = Helpers.CreateHandleInnerHole(
        this.parameters.handleHoleWidth,
        this.parameters.handleHoleLength,
        this.parameters.handleHoleRadius,
        this.parameters.handleHoleDepth
      );
      handleHole.translate(
        this.parameters.frameWidth / 2,
        -this.parameters.handleHoleOffset,
        this.parameters.height / 2
      );
      leftStile = Helpers.BooleanDifference(leftStile, handleHole);
    }

    /* push the geometries into the array */
    geometries.push(this.updateGeometry(leftStile, 90));
    geometries.push(this.updateGeometry(rightStile, 90));
    geometries.push(this.updateGeometry(topRail));
    geometries.push(this.updateGeometry(bottomRail));

    /* create the middle rail */
    for (let i = 0; i < this.parameters.density; i++) {
      const centralRail = Helpers.CreateMiddleRail(
        this.parameters.frameWidth,
        this.parameters.railWidth,
        this.parameters.frameThickness,
        this.parameters.slatThickness,
        this.parameters.frameGroove
      );
      centralRail.translate(
        this.parameters.frameWidth +
          this.parameters.railWidth / 2 -
          this.parameters.frameGroove,
        0,
        (i + 1) *
          (this.parameters.slatLength +
            (this.parameters.frameWidth - 2 * this.parameters.frameGroove))
      );
      geometries.push(this.updateGeometry(centralRail));
    }

    return geometries;
  }

  buildSlatGeometry() {
    const geometries = [];
    /* create the base geometries */
    const slats = Helpers.CreateSlats(
      this.parameters.slatWidth,
      this.parameters.slatLength,
      this.parameters.slatSize,
      this.parameters.slatThickness,
      this.parameters.style
    );

    const slatsAngle = this.getOrientation();

    /* create mirror and duplicate the elements */
    for (let i = 0; i < this.parameters.density + 1; i++) {
      slats.forEach((s) => {
        let slat = s.clone();
        if (i % 2 === 0) {
          slat = Helpers.MirrorObjectZ(slat, false);
          slat.translate(0, 0, this.parameters.slatLength);
        }
        slat.translate(
          this.parameters.frameWidth - this.parameters.frameGroove,
          this.parameters.slatCentering,
          this.parameters.frameWidth -
            this.parameters.frameGroove +
            i *
              (this.parameters.slatLength +
                this.parameters.frameWidth -
                2 * this.parameters.frameGroove)
        );
        geometries.push(
          this.updateGeometry(slat, Math.pow(-1, i) * slatsAngle)
        );
      });
    }
    slats.forEach((s) => {
      s.dispose();
    }); // Clean geometries to avoid leak

    return geometries;
  }

  buildBodyGeometry() {
    const geometries = [];
    /* Create the base objects */
    const plate = Helpers.Box2Points(
      new Vector3(
        this.parameters.frameWidth - this.parameters.frameGroove,
        this.parameters.mirrorThickness - this.parameters.slatThickness / 2,
        this.parameters.frameWidth - this.parameters.frameGroove
      ),
      new Vector3(
        this.parameters.frameWidth -
          this.parameters.frameGroove +
          this.parameters.slatWidth,
        this.parameters.mirrorThickness + this.parameters.slatThickness / 2,
        this.parameters.frameWidth -
          this.parameters.frameGroove +
          this.parameters.slatLength
      )
    );

    const bodyAngle = this.getOrientation();

    /* Copy the element for each the cell */
    for (let i = 0; i <= this.parameters.density; i++) {
      if (i === 0) {
        geometries.push(
          this.updateGeometry(plate, Math.pow(-1, 1) * bodyAngle)
        );
      } else {
        const tmp = plate.clone();
        tmp.translate(
          0,
          0,
          i *
            (this.parameters.slatLength +
              this.parameters.frameWidth -
              2 * this.parameters.frameGroove)
        );
        if (this.parameters.style === 'oblic') {
          geometries.push(
            this.updateGeometry(tmp, Math.pow(-1, i + 1) * bodyAngle)
          );
        } else {
          geometries.push(
            this.updateGeometry(tmp, Math.pow(-1, 1) * bodyAngle)
          );
        }
      }
    }

    return geometries;
  }

  buildMirrorGeometry() {
    const geometries = [];
    /* Create the base objects */
    const plate = Helpers.Box2Points(
      new Vector3(
        this.parameters.frameWidth - this.parameters.frameGroove,
        -(this.parameters.mirrorThickness + this.parameters.slatThickness) / 2,
        this.parameters.frameWidth - this.parameters.frameGroove
      ),
      new Vector3(
        this.parameters.frameWidth -
          this.parameters.frameGroove +
          this.parameters.slatWidth,
        (this.parameters.mirrorThickness - this.parameters.slatThickness) / 2,
        this.parameters.frameWidth -
          this.parameters.frameGroove +
          this.parameters.slatLength
      )
    );

    /* Copy the element for each the cell */
    for (let i = 0; i <= this.parameters.density; i++) {
      if (i === 0) {
        geometries.push(plate);
      } else {
        const tmp = plate.clone();
        tmp.translate(
          0,
          0,
          i *
            (this.parameters.slatLength +
              this.parameters.frameWidth -
              2 * this.parameters.frameGroove)
        );
        geometries.push(tmp);
      }
    }

    return geometries;
  }

  buildBraceGeometry() {
    const geometries = [];
    /* create the base geometries */
    const braceWidth = this.parameters.width - 2 * this.parameters.frameWidth;
    const braceHeight =
      (this.parameters.height -
        (2 + this.parameters.density) * this.parameters.frameWidth) /
      (this.parameters.density + 1);
    const { list, theta } = Helpers.CreateBrace(
      braceWidth,
      braceHeight,
      this.parameters.braceWidth,
      this.parameters.braceThickness,
      this.parameters.brace
    );

    /* mirror the element based on the density */
    for (let i = 0; i < this.parameters.density + 1; i++) {
      let index = 0;
      list.forEach((b) => {
        let brace = b.clone();
        if (i % 2 !== 0 && this.parameters.brace === braceStyle.braced) {
          brace = Helpers.MirrorObjectZ(brace, false);
          brace.translate(0, 0, braceHeight);
        }
        brace.translate(
          this.parameters.frameWidth,
          -this.parameters.frameGroove, //  the frame depth
          (i + 1) * this.parameters.frameWidth + i * braceHeight
        );
        if (this.parameters.brace === braceStyle.braced) {
          geometries.push(
            this.updateGeometry(
              brace,
              90 - Math.pow(-1, (i + 1) % 2) * Math.pow(-1, index) * theta
            )
          );
        } else {
          geometries.push(
            this.updateGeometry(brace, 90 + Math.pow(-1, index) * theta)
          );
        }
        index++;
      });

      list.forEach((b) => {
        b.dispose();
      }); // Clean geometries to avoid leak
    }

    return geometries;
  }

  buildHandleGeometry() {
    const geometries = [];
    const handleObj = this.resourceManager.getObject('handleInner');
    if (!handleObj) {
      return [];
    }

    const handle = handleObj.children[0].geometry.clone();

    Helpers.RotateObject(handle, null, 90, 'X');
    handle.translate(0, -this.parameters.handleOffset, 0);
    const handleClone = handle.clone();
    handleClone.rotateX(Math.PI);

    if (this.parameters.orientation === 'left') {
      handle.translate(
        this.parameters.frameWidth / 2,
        0,
        this.parameters.height / 2
      );
      handleClone.translate(
        this.parameters.frameWidth / 2,
        0,
        this.parameters.height / 2
      );
    } else {
      handle.translate(
        this.parameters.width - this.parameters.frameWidth / 2,
        0,
        this.parameters.height / 2
      );
      handleClone.translate(
        this.parameters.width - this.parameters.frameWidth / 2,
        0,
        this.parameters.height / 2
      );
    }

    geometries.push(handle);
    geometries.push(handleClone);

    return geometries;
  }

  buildHangerGeometry() {
    const geometries = [];

    let hangerObj;
    if (this.parameters.system === 'hamak') {
      hangerObj = this.resourceManager.getObject('hamakSystem');
    } else if (this.parameters.system === 'roller') {
      hangerObj = this.resourceManager.getObject('rollerSystem');
    } else if (this.parameters.system === 'hidden') {
      hangerObj = this.resourceManager.getObject('hiddenSystem');
    } else if (this.parameters.system === 'simple') {
      hangerObj = this.resourceManager.getObject('simpleSystem');
    }
    if (!hangerObj) {
      return [];
    }

    hangerObj.children.forEach((z) => {
      const geom = z.geometry.clone();
      Helpers.RotateObject(geom, null, 90, 'X');

      geom.translate(
        this.parameters.hangerMargin,
        this.parameters.hangerOffset,
        this.parameters.height + this.parameters.hangerHeightOffset
      );
      const geomClone = geom.clone();
      geomClone.translate(
        this.parameters.width - 2 * this.parameters.hangerMargin,
        0,
        0
      );

      geometries.push(geom);
      geometries.push(geomClone);
    });

    return geometries;
  }

  getOrientation() {
    if (this.parameters.style === 'horizontal') {
      return 0;
    }
    if (this.parameters.style === 'oblic') {
      return 45;
    }
    return 90;
  }
}
